import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/styllight";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const styllight: CaseStudy = {
  slug: "styllight",
  title: "Styllight",
  cardTitle: "Разработка и продвижение интернет-магазина освещения Styllight",
  subtitle: "Интернет-магазин дизайнерского освещения",
  industry: { label: "Дом, мебель, свет", slug: "furniture" },
  eyebrow: "Интернет-магазин · Освещение и интерьерные решения",
  h1: "Разработка и продвижение интернет-магазина Styllight",
  lead:
    "Собрали интернет-магазин Styllight: каталог светильников, люстр, линейных систем и профильных решений, отдельный сценарий для дизайнеров и архитекторов, расчёт и цену по запросу — и ведём продвижение проекта.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },

  seo: {
    primaryKeyword: "разработка и продвижение интернет-магазина освещения",
    secondaryKeywords: [
      "интернет-магазин светильников разработка",
      "сайт производителя освещения",
      "продвижение магазина светильников",
      "e-commerce для интерьерного бренда",
    ],
    semanticEntities: [
      "каталог освещения",
      "светильники и люстры",
      "линейные системы",
      "профильные решения",
      "источники питания",
      "B2B-сценарий для дизайнеров",
      "расчёт и заявка",
      "цена по запросу",
    ],
    excludedKeywords: ["купить светильник", "люстра цена", "трековый свет", "светильники Москва"],
    title: "Интернет-магазин Styllight — кейс HAY.agency",
    description:
      "Кейс HAY.agency: интернет-магазин дизайнерского освещения с каталогом, B2B-сценарием для дизайнеров, расчётами и продвижением.",
    h1: "Разработка и продвижение интернет-магазина Styllight",
    slug: "styllight",
    canonicalUrl: `${SITE_URL}/cases/styllight`,
    ogTitle: "Интернет-магазин дизайнерского освещения Styllight",
    ogDescription:
      "Каталог светильников и систем освещения, B2B-сценарий для дизайнеров, расчёт и цена по запросу, продвижение проекта.",
    ogImage: `${SITE_URL}${dir}/styllight-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог технического продукта и B2B-сценарии",
    },
    relatedIndustry: {
      label: "Разработка сайтов для интерьерных и мебельных брендов",
      to: routes.ecom,
      note: "Технически сложный продукт и подбор по параметрам",
    },
    relatedCases: [
      {
        label: "Сайт-каталог светового оборудования для производителя заборов Artline",
        to: "/cases/artline",
        note: "Технически сложный продукт, разложенный на комплектации",
      },
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Большой товарный каталог и карточка товара",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин технически сложного продукта?",
    text: "Спроектируем каталог, B2B-сценарий и расчёт стоимости, соберём магазин и возьмём продвижение на себя.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "Styllight",
    url: "https://styllight.ru/",
    city: "Москва",
    industry: "Освещение и интерьерные решения",
  },
  year: "С 2024 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога и карточки товара",
    "B2B-сценарий для дизайнеров и архитекторов",
    "Формы расчёта и заявки",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Styllight" },
    { label: "Отрасль", value: "Освещение и интерьерные решения" },
    { label: "Тип проекта", value: "Интернет-магазин с B2B-сценарием" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог светильников, люстр, линейных систем, профильных решений, источников питания и подсветки",
      "Карточка товара с техническими характеристиками",
      "Отдельный сценарий сотрудничества для дизайнеров, архитекторов и строительных организаций",
      "Формы расчёта и заявки, цена по запросу",
      "Корзина и обратный звонок",
      "Несколько точек контакта в Москве",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Освещение — технический продукт: покупатель выбирает конструктив, мощность, тип подключения и совместимость с профильной системой. Магазину нужен был каталог, который выдерживает такую детализацию и не превращается в перечень характеристик без структуры.",
        "Вторая часть задачи — развести розничный и профессиональный сценарии: дизайнеры, архитекторы и строительные организации подбирают освещение иначе, чем частный покупатель, и им нужен собственный вход в каталог.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: продукт на графитово-каменной подложке",
    paragraphs: [
      "Интерфейс построен на графитово-каменной палитре — она подчёркивает предметный вид светильников и не спорит с их формой на витрине.",
      "Каталог и карточка товара оформлены единообразно: фотография продукта, характеристики и переход к расчёту или заявке расположены в одном порядке на каждой странице.",
    ],
    bullets: [
      "Графитово-каменная палитра вместо чистого белого",
      "Единая структура карточки для розницы и B2B",
      "Цена по запросу и расчёт доступны на каждом шаге каталога",
    ],
  },

  identity: {
    fonts: [
      { name: "Graphik LCG", role: "Заголовки", sample: "Каталог освещения" },
      { name: "Graphik LCG", role: "Текст и характеристики", sample: "Линейные системы" },
    ],
    colors: [
      { hex: "#2B2B2C", name: "Графит" },
      { hex: "#D8D2C7", name: "Камень" },
      { hex: "#F5F3EF", name: "Фон" },
    ],
  },

  cover: img(
    "styllight-case-cover",
    "Главная страница интернет-магазина освещения Styllight",
    6,
  ),

  gallery: [
    img("styllight-catalog", "Каталог и карточка товара интернет-магазина Styllight", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия строилась вокруг тех же сценариев, что и десктоп: выбор категории, карточка товара с характеристиками и переход к расчёту или заявке.",
      "Корзина и B2B-заявка доступны с одного экрана, без возврата в каталог.",
    ],
    images: [
      mobile("styllight-mobile-home", "Мобильная главная интернет-магазина Styllight"),
      mobile("styllight-mobile-catalog", "Мобильный каталог освещения Styllight"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог технического продукта",
      paragraphs: [
        "Каталог разложен по категориям продукта, а карточка товара показывает характеристики, необходимые для подбора: конструктив, мощность, тип подключения и совместимость с профильными решениями.",
      ],
      columns: [
        { title: "Категории", items: ["Светильники", "Люстры", "Линейные системы"] },
        { title: "Комплектующие", items: ["Профильные решения", "Источники питания", "Подсветка"] },
        { title: "Карточка товара", items: ["Характеристики", "Фотографии", "Цена по запросу"] },
      ],
      images: [
        img("styllight-catalog", "Каталог интернет-магазина Styllight с фильтрами и карточкой товара", 6),
      ],
      caption: "Каталог не выводит десятки категорий текстом — только структурированный выбор.",
    },
    {
      id: "b2b",
      title: "Розница и профессиональный сценарий",
      paragraphs: [
        "Помимо розничной покупки на сайте работает отдельный вход для дизайнеров, архитекторов и строительных организаций — с собственными условиями сотрудничества.",
      ],
      bullets: [
        "Отдельный сценарий для дизайнеров и архитекторов",
        "Условия для строительных организаций",
        "Общая карточка товара для розницы и B2B",
      ],
      images: [
        img("styllight-b2b", "B2B-раздел и заявка на расчёт в интернет-магазине Styllight", 6),
      ],
    },
    {
      id: "calc",
      title: "Расчёт и цена по запросу",
      paragraphs: [
        "Цена на техническую продукцию зависит от конфигурации, поэтому в магазине работают форма расчёта, консультация и цена по запросу — как самостоятельные бизнес-функции, а не заглушки вместо стоимости.",
      ],
      bullets: [
        "Форма расчёта в карточке товара",
        "Цена по запросу и консультация",
        "Корзина и обратный звонок",
      ],
    },
    {
      id: "promotion",
      title: "Продвижение проекта",
      paragraphs: [
        "Магазин остаётся на нашем сопровождении: работаем с поисковой видимостью каталога и развиваем проект. Показатели раскроем после согласования с клиентом.",
      ],
      bullets: [
        "SEO каталога и карточек товара",
        "Развитие структуры магазина",
        "Техническая поддержка проекта",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Поддержка и развитие", to: routes.ecom },
    ],
  },
};
